import { Stack } from "@mui/material"
import {
    Button,
    DataTable,
    DialogHandler,
    EmptyContent,
    fetchRoute,
    GenericWrapperContext,
    LoadingContainer,
    Search,
    TDataTableColumnsData,
    TGenericListReturn,
    TSuppressedEmail,
    SuppressedEmailsFactory,
    ETaskType,
    FullBar,
    Header,
    Icon,
    Dropdown
} from "@cakemail-org/ui-components-v2"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { TSuppressionListing } from "../types"
import { useContext, useEffect, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { showTaskDialog } from "../../../components/dialogs/export"
import { ERoutes } from "../../../utils/url"
import { EPlanLimitsFeatures, showPlanLimitsDialog } from "../../../utils/limits_features"

export default function SuppressionListing({ }: TSuppressionListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()

    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(25)
    const [suppressedEmails, setSuppressedEmails] = useState<TGenericListReturn<TSuppressedEmail>>()
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState<string>("")
    const [openedSearch, setOpenedSearch] = useState(false)

    useEffect(() => {
        fetchSuppressedEmails()
    }, [page, perPage, search])

    useEffect(handleSearchFocus, [openedSearch])

    function fetchSuppressedEmails() {
        setLoading(true)
        SuppressedEmailsFactory.list({
            page,
            perPage,
            filter: search ? `email==*${search}*` : undefined,
            withCount: true
        }).then((res) => {
            setSuppressedEmails(res)
            setLoading(false)
        }).catch(() => {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("suppression-list-fetch-list-error")
            })
            setLoading(false)
        })
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }

    function handleSearchOpen() {
        setOpenedSearch(!openedSearch)
    }

    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
        setPerPage(cPerPage)
    }

    function handleGoToExportsPage() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.export })
    }

    function handleExportSuppressionList() {
        if (showPlanLimitsDialog({
            name: EPlanLimitsFeatures.use_suppressed_emails_export,
            account: genericWrapper.account,
            user: genericWrapper.user,
            partnerBrand: genericWrapper.partnerBrand
        })) {
            return
        }
        showTaskDialog({
            taskType: ETaskType.suppressed_emails_export
        })
    }

    function handleAddToSuppressionList() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.suppressionListAdd }) + "?step=select"
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".suppressionListSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleDeleteSuppressedItem(email: TSuppressedEmail) {
        if (email.source !== 'manual') {
            DialogHandler.show({
                isExpress: true,
                type: "warning",
                title: t("suppression-list-delete-non-manual-email-error")
            })
            return
        }

        SuppressedEmailsFactory.delete({ email: email.email })
            .then(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "success",
                    title: t("suppression-list-unsuppress-email-success", { email: email.email })
                })
                fetchSuppressedEmails()
            })
            .catch(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("general-error-action")
                })
            })
    }

    const dropdownOptions: TDropMenuItem[] = useMemo(() => {

        return [
            {
                isAction: true,
                value: "export-suppression-list",
                text: t("suppression-list-export-list"),
                onClick: handleExportSuppressionList
            },
            {
                isAction: true,
                value: "view-exports",
                text: t("general-view-exports"),
                onClick: handleGoToExportsPage
            }
        ]
    }, [])

    const colsData: TDataTableColumnsData[] = useMemo(() => [
        {
            id: "email",
            label: t("suppression-list-column-heading-email-domain")
        },
        {
            id: "source",
            label: t("suppression-list-column-heading-source")
        },
        {
            id: "button",
            label: ""
        }
    ], [])

    const rows: any[] = suppressedEmails && suppressedEmails.data.map((suppressedItem: TSuppressedEmail, index) => {
        const actions: any = (
            <Stack direction="row" gap={2}>
                <Button
                    onClick={() => handleDeleteSuppressedItem(suppressedItem)}
                    size="small"
                    color="body1"
                    variant="outlined"
                    disabled={loading}
                >
                    {t("general-delete")}
                </Button>
            </Stack>
        )

        return {
            id: index,
            email: suppressedItem.email || "",
            source: suppressedItem.source || "",
            button: actions
        }
    }) || []

    return (
        <Stack gap={8}>
            <FullBar
                className={loading ? "loading" : ""}
                left={<Header
                    heading={t("general-suppression-list")}
                    headingVariant="titleL"
                    support={genericWrapper.account?.name}
                    headingTooltip={t("suppression-list-heading-tooltip")}
                />}
                right={[
                    <Button
                        key="add-to-suppression-list"
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleAddToSuppressionList}
                        iconOnly
                        disabled={loading}
                    >
                        <Icon name="Add18" />
                    </Button>,
                    <Button
                        variant="outlined"
                        size="medium"
                        color="body1"
                        onClick={handleSearchOpen}
                        iconOnly
                        disabled={loading}
                    >
                        <Icon name="Search18" />
                    </Button>,
                    <Dropdown
                        key="exports-dropdown"
                        options={dropdownOptions}
                        placeholder={t("general-export_other")}
                        size="medium"
                        color="body1"
                        variant="outlined"
                    />
                ]}
            />

            {openedSearch &&
                <Search placeholder={t("suppression-list-search-placeholder")} initValue={search} className={`suppressionListSearch ${loading ? "loading" : ""}`} onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
            }

            <LoadingContainer loading={!suppressedEmails}>

                {suppressedEmails && suppressedEmails?.data?.length === 0 && !search &&
                    <EmptyContent
                        title={t("suppression-list-listing-empty-title")}
                        desc={t("suppression-list-listing-empty-desc")}
                        iconName="CreateForm44"
                        button={<Button
                            variant="contained"
                            onClick={handleAddToSuppressionList}
                            size="medium"
                            color="secondary"
                            iconOnly>
                            {t("general-add-to-suppression-list")}
                        </Button>}
                    />
                }

                {suppressedEmails && suppressedEmails.data.length === 0 && search &&
                    <EmptyContent
                        title={t("suppression-list-listing-empty-title")}
                        desc={t("search-empty-desc")}
                    />
                }

                {suppressedEmails && suppressedEmails.data.length > 0 &&
                    <DataTable
                        initState={{
                            rowsPerPage: perPage
                        }}
                        totalCount={suppressedEmails?.pagination?.count}
                        className={`suppressionListDataTable ${loading ? "loading" : ""}`}
                        onPaginationChange={handlePaginationChange}
                        rows={rows}
                        columnsData={colsData}
                        stickyLastCol
                        showPagination={true}
                    />
                }

            </LoadingContainer>
        </Stack>
    )
}